import dayjs from 'dayjs';

/**
 * Generates and downloads a sample PDF report
 * @param reportType - Type of report (investigation, claims, provider, etc.)
 * @param fileName - Name for the downloaded file
 */
export const downloadSamplePDF = (reportType: string = 'Report', fileName?: string) => {
  const defaultFileName = fileName || `${reportType.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD_HHmmss')}.pdf`;
  
  // Create a simple PDF-like content (mock data)
  const pdfContent = generatePDFContent(reportType);
  
  // Convert to blob
  const blob = new Blob([pdfContent], { type: 'application/pdf' });
  
  // Create download link
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = defaultFileName;
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Generates mock PDF content
 */
const generatePDFContent = (reportType: string): string => {
  const currentDate = dayjs().format('MMMM D, YYYY HH:mm:ss');
  
  return `
%PDF-1.4
1 0 obj
<<
/Type /Catalog
/Pages 2 0 R
>>
endobj
2 0 obj
<<
/Type /Pages
/Kids [3 0 R]
/Count 1
>>
endobj
3 0 obj
<<
/Type /Page
/Parent 2 0 R
/Resources <<
/Font <<
/F1 4 0 R
>>
>>
/MediaBox [0 0 612 792]
/Contents 5 0 R
>>
endobj
4 0 obj
<<
/Type /Font
/Subtype /Type1
/BaseFont /Helvetica-Bold
>>
endobj
5 0 obj
<<
/Length 500
>>
stream
BT
/F1 24 Tf
50 750 Td
(${reportType}) Tj
0 -40 Td
/F1 12 Tf
(Generated: ${currentDate}) Tj
0 -30 Td
(Report ID: ${Math.random().toString(36).substr(2, 9).toUpperCase()}) Tj
0 -30 Td
(Status: SAMPLE REPORT) Tj
0 -50 Td
(This is a sample PDF report generated by Hirsto Insurance Platform.) Tj
0 -30 Td
(In production, this would contain:) Tj
0 -25 Td
(- Detailed investigation findings) Tj
0 -20 Td
(- Provider information and analytics) Tj
0 -20 Td
(- Claims data and fraud indicators) Tj
0 -20 Td
(- Network graphs and correlations) Tj
0 -20 Td
(- Recommended actions and enforcement) Tj
0 -20 Td
(- Legal documentation for regulatory submission) Tj
0 -50 Td
(Hirsto Health Insurance Platform) Tj
0 -20 Td
(Confidential & Proprietary) Tj
ET
endstream
endobj
xref
0 6
0000000000 65535 f
0000000009 00000 n
0000000058 00000 n
0000000115 00000 n
0000000274 00000 n
0000000361 00000 n
trailer
<<
/Size 6
/Root 1 0 R
>>
startxref
913
%%EOF
`;
};

/**
 * Downloads investigation report PDF
 */
export const downloadInvestigationReport = (providerName?: string) => {
  const fileName = providerName 
    ? `Investigation_Report_${providerName.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.pdf`
    : undefined;
  downloadSamplePDF('Investigation Report', fileName);
};

/**
 * Downloads provider analysis report PDF
 */
export const downloadProviderReport = (providerName?: string) => {
  const fileName = providerName 
    ? `Provider_Analysis_${providerName.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.pdf`
    : undefined;
  downloadSamplePDF('Provider Analysis Report', fileName);
};

/**
 * Downloads claims data export PDF
 */
export const downloadClaimsReport = () => {
  downloadSamplePDF('Claims Data Export', `Claims_Report_${dayjs().format('YYYY-MM-DD')}.pdf`);
};

/**
 * Downloads network graph data
 */
export const downloadNetworkGraphData = () => {
  downloadSamplePDF('Network Graph Analysis', `Network_Analysis_${dayjs().format('YYYY-MM-DD')}.pdf`);
};
